// Action Types
export const ActionTypes = {
  SET_ROBOT_CLIENTS: "SET_ROBOT_CLIENTS",
  SET_USER_ROBOTS_DATA: "SET_USER_ROBOTS_DATA",
  SET_USER_ORGS_DATA: "SET_USER_ORGS_DATA",
  SET_ROBOT_CONNECTED: "SET_ROBOT_CONNECTED",
  SET_ROBOT_DISCONNECTED: "SET_ROBOT_DISCONNECTED",
  UPDATE_ROBOT_CLIENT_DATA: "UPDATE_ROBOT_CLIENT_DATA",
};

// Robot Icon Mapping
const ROBOT_ICONS = {
  gocart: "images/gocart.png",
  spot: "images/spot.jpg",
  syscon: "images/syscon.png",
  tracer: "images/tracer.png",
  demobot2: "images/demobot2.png",
};

const get_robot_icon = (robot) => {
  return ROBOT_ICONS[robot.type] || "images/unknown_robot_type.png";
};

const hasCamera = (robot) => {
  const cameras = robot?.setting?.cameras || [];
  return cameras.some((camera) => camera?.class_name !== "DummyCamera");
};

const hasSpeaker = (robot) => {
  const speakers = robot?.setting?.speakers || [];
  return speakers.length > 0;
};

const hasMicrophones = (robot) => {
  const microphones = robot?.setting?.microphones || [];
  return microphones.length > 0;
};

// Helper để normalize array thành { byId, allIds }
const normalizeArray = (array, idKey = "id") => {
  const byId = {};
  const allIds = [];

  array.forEach((item) => {
    const id = item[idKey];
    if (id != null) {
      byId[id] = item;
      allIds.push(id);
    }
  });

  return { byId, allIds };
};

const group_robot_connections = (robots) => {
  const robotGroups = {};

  for (let index = 0; index < robots.length; index++) {
    const robot_info = robots[index];
    const settings = robot_info?.setting || {};
    const is_control_channel = settings?.is_control_channel || false;
    const has_camera = hasCamera(robot_info);
    const has_speaker = hasSpeaker(robot_info);
    const has_microphones = hasMicrophones(robot_info);

    const robot = {
      ...robot_info,
      has_camera: has_camera,
      has_speaker: has_speaker,
      has_microphones: has_microphones,
    };

    const group_id = robot?.robot_id ?? "";

    const connecting_user_clients = robot?.connecting_user_clients
      ? Object.entries(robot.connecting_user_clients)
      : [];

    if (robotGroups[group_id]) {
      robotGroups[group_id] = {
        ...robotGroups[group_id],
        connections: [...robotGroups[group_id].connections, robot],
        status:
          robot.status === "online" ? "online" : robotGroups[group_id].status,
        control_connections:
          is_control_channel === false
            ? [...robotGroups[group_id].control_connections]
            : [...robotGroups[group_id].control_connections, robot.id],
        connecting_user_clients: [
          ...robotGroups[group_id].connecting_user_clients,
          ...connecting_user_clients,
        ],
      };
    } else {
      robotGroups[group_id] = {
        group_id: group_id,
        ...robot,
        icon: get_robot_icon(robot),
        robot_id: group_id,
        connections: [robot],
        status: robot.status,
        control_connections: is_control_channel === false ? [] : [robot.id],
        connecting_user_clients: connecting_user_clients,
      };
    }
  }
  return robotGroups;
};

// Helper function to update robot clients and groups
const updateRobotClientsAndGroups = (state, updateFn) => {
  // Convert normalized state back to array for processing
  const robotsArray = state.robotClients.allIds.map(
    (id) => state.robotClients.byId[id],
  );
  const updatedRobotClients = robotsArray.map(updateFn);

  // Normalize the updated array back to normalized state
  const normalized = normalizeArray(updatedRobotClients, "id");
  const updatedRobotGroupList = group_robot_connections(updatedRobotClients);

  return {
    ...state,
    robotClients: normalized,
    robotClientGroups: updatedRobotGroupList,
  };
};

export const userDataReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.SET_ROBOT_CLIENTS: {
      const robots = [...action.payload];
      const normalized = normalizeArray(robots, "id");
      const robotGroups = group_robot_connections(robots);

      return {
        ...state,
        robotClients: normalized,
        robotClientGroups: robotGroups,
      };
    }

    case ActionTypes.SET_USER_ROBOTS_DATA: {
      const {
        page,
        per_page,
        total,
        results = [],
      } = action?.payload || {
        page: 1,
        per_page: 5,
        total: 0,
        results: [],
      };

      const normalized = normalizeArray(results, "robot_id");

      return {
        ...state,
        robots: {
          byId: normalized.byId,
          allIds: normalized.allIds,
          page,
          per_page,
          total,
        },
      };
    }

    case ActionTypes.SET_USER_ORGS_DATA: {
      const {
        page,
        per_page,
        total,
        results = [],
      } = action?.payload || {
        page: 1,
        per_page: 5,
        total: 0,
        results: [],
      };
      const normalized = normalizeArray(results, "org_id");

      return {
        ...state,
        organizations: {
          byId: normalized.byId,
          allIds: normalized.allIds,
          page,
          per_page,
          total,
        },
      };
    }

    case ActionTypes.SET_ROBOT_CONNECTED: {
      const robot_id = action.payload.id;
      return updateRobotClientsAndGroups(state, (rb) =>
        rb.id === robot_id ? { ...rb, ...action.payload } : rb,
      );
    }

    case ActionTypes.SET_ROBOT_DISCONNECTED: {
      const robot_id = action.payload.id;
      return updateRobotClientsAndGroups(state, (rb) =>
        rb.id === robot_id
          ? { ...rb, ...action.payload, status: "offline" }
          : rb,
      );
    }
    case ActionTypes.UPDATE_ROBOT_CLIENT_DATA: {
      const { robot_client_id, updated_data } = action.payload;
      return updateRobotClientsAndGroups(state, (rb) =>
        rb.id === robot_client_id ? { ...rb, ...updated_data } : rb,
      );
    }

    default:
      return state;
  }
};

export const initialUserData = {
  robotClients: {
    byId: {},
    allIds: [],
  },
  /**
   * Each element structure:
   * robot_id: {
   *    byRobotId: {
   *       robot_client_id: { ...robotClientData }
   *    },
   *    byClientId: {
   *       robot_client_id: { ...robotClientData }
   *    }
   * }
   */

  robotConnectionInfo: {
    byRobotId: {},
    byClientId: {},
  },

  robotClientGroups: {},

  // robot's clients info

  // user's robots and roles state
  robots: {
    byId: {},
    allIds: [],
    page: 1,
    per_page: 10,
    total: 0,
  },
  // user's organizations and roles state
  organizations: {
    byId: {},
    allIds: [],
    page: 1,
    per_page: 10,
    total: 0,
  },

  // organization data structure
  orgs: {
    org_id: "id",
    name: "name",
    roles: ["role1", "role2"],
  },

  // robot data structure
  // robots: {
  //   robot_id: "id",
  //   name: "name",
  //   type: "type",
  //   icon: "icon_url",
  //   status: "online/offline",
  //   organization_id: "org_id",
  //   roles: ["role1", "role2"],
  //   settings: { ...robotSettings },
  //   cameras: [],
  //   speakers: []],
  //   microphones: [],
  // },
};
